import { getSupabaseClient } from '@/config';

/** Translator, passed in: these messages are read on the connect form. */
export type TFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * TikTok advertiser validation (client side).
 *
 * TikTok's Business API sends no CORS headers, so the browser can't call it
 * directly (unlike Facebook). This posts to the server proxy /api/tiktok-validate,
 * which calls TikTok's /advertiser/info/ server-side. Only the user's own token
 * is sent — no app secret is involved.
 */

export interface TikTokAccountValidation {
  valid: boolean;
  accountName?: string;
  error?: string;
}


/** The caller's session, which the validate proxies require. */
const authHeader = async (): Promise<Record<string, string>> => {
  const { data } = await getSupabaseClient().auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export async function validateTikTokAdvertiser(
  advertiserId: string,
  token: string,
  // Unused: TikTok has no second credential field. Kept so every validator
  // shares one signature — that is what lets manualMethods hold them in a
  // single typed table.
  _secret: string | undefined,
  t: TFn,
): Promise<TikTokAccountValidation> {
  try {
    const resp = await fetch('/api/tiktok-validate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // The proxy is a signed-in-only route now: it takes a credential and
        // reports whether it works, which is not something to offer the world.
        ...(await authHeader()),
      },
      body: JSON.stringify({ access_token: token, advertiser_id: advertiserId }),
    });
    const data = (await resp.json().catch(() => null)) as Partial<TikTokAccountValidation> & { error?: string } | null;
    if (data && typeof data.valid === 'boolean') {
      return { valid: data.valid, accountName: data.accountName, error: data.error };
    }
    return { valid: false, error: data?.error ?? t('settings.connectFailed', { platform: 'TikTok' }) };
  } catch {
    return { valid: false, error: t('settings.connectFailed', { platform: 'TikTok' }) };
  }
}
